// Largest Remainder Method to ensure integer percentages sum to 100
export const computeRoundedPercentages = (values) => {
    const safeValues = Array.isArray(values) ? values.map(v => (typeof v === 'number' ? v : 0)) : [];
    const total = safeValues.reduce((s, v) => s + v, 0);
    if (total === 0) return safeValues.map(() => 0);

    const raw = safeValues.map(v => (v / total) * 100);
    const base = raw.map(p => Math.floor(p));
    let remainder = 100 - base.reduce((a, b) => a + b, 0);
    const frac = raw.map((p, i) => ({ i, frac: p - Math.floor(p) }))
        .sort((a, b) => b.frac - a.frac);
    for (let k = 0; k < frac.length && remainder > 0; k++) {
        base[frac[k].i] += 1;
        remainder -= 1;
    }
    return base;
};

// Factory that returns a recharts <Pie> `label` callback rendering rounded
// integer percentages centered inside each slice. The factory is needed
// because percentages must be computed up-front from the full dataset
// (via `computeRoundedPercentages`) so each label can be looked up by
// slice index — recharts only hands us the index, not the dataset.
//
// Used by `OverviewPieChart` and the Analytics dashboard pie chart so
// both share identical label styling. Returns plain SVG via
// React.createElement so this util file can remain a `.js` (no JSX).
import React from 'react';

export const buildPieSlicePercentLabel = (values, { fill = 'white', fontSize = 10 } = {}) => {
    const percents = computeRoundedPercentages(values);
    return ({ cx, cy, midAngle, innerRadius, outerRadius, index }) => {
        const RADIAN = Math.PI / 180;
        const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);
        return React.createElement(
            'text',
            {
                x,
                y,
                fill,
                textAnchor: 'middle',
                dominantBaseline: 'central',
                fontSize,
            },
            `${percents[index] ?? 0}%`,
        );
    };
};

export const buildOverviewPieData = ({
    isRelationsTab,
    counts: {
        successful = 0,
        miscategorized = 0,
        misaligned = 0,
        misalignedMiscategorized = 0,
        missed = 0,
        fabricated = 0,
        outOfContent = 0,
    } = {}
}) => {
    const partial = miscategorized + misaligned + misalignedMiscategorized;
    const failed = isRelationsTab
        ? (missed + fabricated + miscategorized)
        : (missed + fabricated + outOfContent);

    const base = isRelationsTab
        ? [
            {
                name: `Failed (${failed})`,
                value: failed,
                tooltipLines: [
                    ...(missed > 0 ? [`${missed} missed relations`] : []),
                    ...(fabricated > 0 ? [`${fabricated} fabricated relations`] : []),
                    ...(miscategorized > 0 ? [`${miscategorized} miscategorized relations`] : []),
                ],
                color: '#ef4444'
            },
            { name: `Successful (${successful})`, value: successful, tooltipText: `${successful} Correctly extracted relations`, color: '#53c31b' }
        ]
        : [
            {
                name: `Failed (${failed})`,
                value: failed,
                tooltipLines: [
                    ...(missed > 0 ? [`${missed} missed entities`] : []),
                    ...(fabricated > 0 ? [`${fabricated} fabricated entities`] : []),
                    ...(outOfContent > 0 ? [`${outOfContent} out of content entities`] : []),
                ],
                color: '#ef4444'
            },
            {
                name: `Partial (${partial})`,
                value: partial,
                tooltipLines: [
                    ...(miscategorized > 0 ? [`${miscategorized} miscategorized entities`] : []),
                    ...(misaligned > 0 ? [`${misaligned} misaligned entities`] : []),
                    ...(misalignedMiscategorized > 0 ? [`${misalignedMiscategorized} misaligned and miscategorized entities`] : []),
                ],
                color: '#faad14'
            },
            { name: `Successful (${successful})`, value: successful, tooltipText: `${successful} Correctly extracted entities`, color: '#53c31b' }
        ];

    return base.filter(d => (typeof d.value === 'number' ? d.value : 0) > 0);
};

